'use client'

import { useEffect } from 'react'
import debounce from 'lodash.debounce'
import { ParsedUrlQuery } from 'querystring'
import { isNil } from 'ramda'

import { useInfinitePagination } from 'shared/hooks/useInfinitePagination'

import { Brand, ProductTypes } from '../types/products'

type getNextPageCallbackParams = {
  token?: string
  query: ParsedUrlQuery
  slug?: string
  limit?: number
  productIds?: string[]
  /** Always set by useInfiniteScroll (from useDistributionCenterId) for entity / listing pagination */
  distributionCenterId: string
}

type useInfiniteScrollProps<T> = {
  products: T[]
  limit?: number
  slug?: string
  query: ParsedUrlQuery
  token?: string
  distributionCenterId?: string
  key?: string
  productIds?: string[]
  getNextPageCallback: (
    pageParam: number,
    {
      token,
      query,
      slug,
      limit,
      productIds,
      distributionCenterId
    }: getNextPageCallbackParams
  ) => Promise<T[]>
}

const useInfiniteScroll = <T extends ProductTypes | Brand>({
  products,
  slug,
  query,
  distributionCenterId = '',
  token,
  getNextPageCallback,
  productIds,
  limit = 40,
  key
}: useInfiniteScrollProps<T>) => {
  //react-query type errors, we need to include new function and ignore for 'queryKey'
  //https://github.com/TanStack/query/issues/1462
  const {
    fetchNextPage,
    isFetching,
    inView,
    ref,
    status,
    items: productsData,
    refetch,
    isFetchingNextPage,
    hasNextPage
  } = useInfinitePagination({
    items: products,
    getNextPageCallback,
    limit,
    params: { slug, query, distributionCenterId, token, productIds },
    key: key || 'productsListing'
  })

  useEffect(() => {
    if (!inView || !isNil(query.category)) {
      return
    }

    const debouncedFetchNextPage = debounce(() => {
      void fetchNextPage()
    }, 50)

    debouncedFetchNextPage()

    return () => {
      debouncedFetchNextPage.cancel()
    }
  }, [inView, fetchNextPage, query.category])

  useEffect(() => {
    const handlingScroll = () => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    }

    window.addEventListener('scroll', handlingScroll)

    return () => {
      window.removeEventListener('scroll', handlingScroll)
    }
    //I don't want trigger this effect if hasNextPage or isFetchingNextPage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, isFetchingNextPage])

  return {
    ref,
    productsData,
    isFetching,
    status,
    refetch,
    isFetchingNextPage
  }
}

export default useInfiniteScroll
